import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus('success');
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9on3aqqs38sj6jc7bs0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(serverMsg || '提交失败，请稍后再试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后再试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "name": "关于虫虫漫画 - 虫虫漫画大全",
        "description": "虫虫漫画是一个专注于漫画在线阅读的平台，汇集韩漫日漫国漫，提供下拉式阅读体验。",
        "url": "https://chongchongmanga.com/about",
        "isPartOf": {
          "@type": "WebSite",
          "name": "虫虫漫画",
          "url": "https://chongchongmanga.com"
        },
        "inLanguage": "zh-CN"
      }) }} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://chongchongmanga.com" },
          { "@type": "ListItem", "position": 2, "name": "关于我们", "item": "https://chongchongmanga.com/about" }
        ]
      }) }} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "虫虫漫画是什么？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "虫虫漫画是一个漫画在线阅读平台，汇集韩漫日漫国漫等多类型的漫画资源，为用户提供免费的在线阅读体验。"
            }
          },
          {
            "@type": "Question",
            "name": "虫虫漫画支持哪些阅读方式？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "虫虫漫画支持下拉式在线阅读，用户只需在页面中向下滚动即可连续阅读漫画内容，无需翻页操作。"
            }
          },
          {
            "@type": "Question",
            "name": "虫虫漫画有哪些漫画分类？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "虫虫漫画大全包含韩漫、日漫、国漫等多种漫画分类，还有完结漫画专区，用户可以按分类浏览喜欢的漫画。"
            }
          },
          {
            "@type": "Question",
            "name": "虫虫漫画是否免费？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "是的，虫虫漫画平台上的所有漫画全集免费在线看，用户无需付费即可享受海量漫画内容。"
            }
          },
          {
            "@type": "Question",
            "name": "如何联系虫虫漫画团队？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "您可以通过虫虫漫画官方网站上的联系表单与我们取得联系，我们会在收到消息后尽快回复。"
            }
          }
        ]
      }) }} />

      <Navbar />

      <main className="pt-20">
        <Breadcrumb items={[
          { label: '首页', href: '/' },
          { label: '关于我们' },
        ]} />

        {/* Hero */}
        <section className="relative w-full h-[320px] md:h-[400px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20artistic%20illustration%20of%20a%20cozy%20reading%20corner%20with%20bookshelves%20full%20of%20comics%2C%20soft%20warm%20lighting%2C%20comfortable%20atmosphere%2C%20anime%20and%20manga%20culture%20inspired%2C%20Japanese%20modern%20interior%2C%20gentle%20coral%20and%20cream%20color%20palette%2C%20minimalist%20elegant%20composition&width=1600&height=600&seq=about-hero&orientation=landscape"
            alt="虫虫漫画 - 关于我们"
            title="虫虫漫画 - 品牌介绍"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/50" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h1 className="font-heading text-3xl md:text-4xl text-background-50 mb-3">
              关于虫虫漫画
            </h1>
            <p className="text-sm md:text-base text-background-50/80 max-w-lg">
              致力于为漫画爱好者提供最优质的在线阅读体验
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="w-full px-4 md:px-6 py-14">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
                品牌故事
              </h2>
              <p className="text-sm text-foreground-500">虫虫漫画大全，从热爱到使命</p>
            </div>
            <div className="space-y-6 text-sm text-foreground-600 leading-relaxed">
              <p>
                <strong>虫虫漫画</strong>诞生于一群漫画爱好者的共同梦想。我们深知，每一个漫画读者都在寻找一个能够畅快阅读、内容丰富的漫画平台。正是这份热爱，驱动我们创建了<strong>虫虫漫画大全</strong>——一个汇集<strong>韩漫</strong>、<strong>日漫</strong>、<strong>国漫</strong>的综合性漫画阅读平台。
              </p>
              <p>
                从创立之初，<strong>虫虫漫画</strong>就坚持"<strong>全集免费在线看</strong>"的理念。我们相信，<strong>好看的漫画</strong>应该被更多人看到，而不应被付费墙所阻隔。无论是热门的<strong>韩漫</strong>连载、经典的<strong>日漫</strong>名作，还是日益崛起的<strong>国漫</strong>精品，<strong>虫虫漫画</strong>平台都致力于为用户提供最全面、最及时的<strong>漫画</strong>资源。
              </p>
              <p>
                <strong>虫虫漫画</strong>独创的<strong>下拉式阅读</strong>模式，让用户无需反复翻页，只需轻轻滑动即可连续阅读。这种沉浸式的阅读体验，正是<strong>虫虫漫画</strong>对"<strong>好看的漫画</strong>"阅读方式的理解与创新。
              </p>
              <p>
                如今，<strong>虫虫漫画大全</strong>已汇聚了超过12,000部优质<strong>漫画</strong>，涵盖<strong>韩漫</strong>、<strong>日漫</strong>、<strong>国漫</strong>等多元题材。从热血战斗到甜蜜恋爱，从奇幻冒险到现实日常，<strong>虫虫漫画全集</strong>满足每一位读者的口味。
              </p>
              <p>
                未来，<strong>虫虫漫画</strong>将继续优化阅读体验，丰富漫画资源，让<strong>虫虫漫画在线看</strong>成为每一位<strong>漫画</strong>爱好者的首选。我们相信，<strong>虫虫漫画</strong>不仅是阅读平台，更是一个连接<strong>漫画</strong>创作者与读者的桥梁。
              </p>
            </div>
          </div>
        </section>

        {/* Stats/Reputation */}
        <section className="w-full bg-background-100/70 py-14">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500">听听虫虫漫画读者们怎么说</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  name: '小明',
                  text: '虫虫漫画真的太棒了！下拉式阅读体验超流畅，韩漫更新也很快，每天必刷虫虫漫画大全！',
                  rating: 5,
                },
                {
                  name: '小美',
                  text: '虫虫漫画全集免费在线看真的良心，日漫资源很全，找到了好多以前找不到的经典作品。',
                  rating: 5,
                },
                {
                  name: '阿杰',
                  text: '国漫专区做得很好，虫虫漫画的界面简洁干净，下拉式阅读模式看漫画特别舒服。',
                  rating: 5,
                },
              ].map((review, idx) => (
                <div key={idx} className="p-5 rounded-lg bg-background-50">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                    "{review.text}"
                  </p>
                  <p className="text-xs text-foreground-500 font-medium whitespace-nowrap">—— {review.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full px-4 md:px-6 py-14">
          <div className="max-w-lg mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500">
                如果您有任何问题或建议，欢迎通过以下表单联系虫虫漫画团队
              </p>
            </div>

            <form
              data-readdy-form="true"
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  您的姓名
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  placeholder="请输入您的姓名"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  电子邮箱
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  placeholder="请输入您的电子邮箱"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  placeholder="请输入主题"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请输入您的留言内容（限500字）"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors resize-none"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {formStatus === 'submitting' ? '提交中...' : '发送留言'}
              </button>

              {formStatus === 'success' && (
                <p className="text-sm text-green-600 text-center">留言发送成功！感谢您的反馈。</p>
              )}
              {formStatus === 'error' && formError && (
                <p className="text-sm text-red-500 text-center">{formError}</p>
              )}
            </form>
          </div>
        </section>

        {/* Brand Info Footer */}
        <section className="w-full bg-background-100/70 py-10">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h3 className="font-heading text-xl text-foreground-900 mb-3">虫虫漫画</h3>
            <p className="text-sm text-foreground-500 leading-relaxed">
              虫虫漫画大全 - 虫虫漫画全集免费在线看 | 好看的漫画下拉式阅读<br />
              韩漫日漫国漫分类推荐，追更收藏尽在虫虫漫画平台
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}